"use client";

import type {
  CancelSubscriptionResponse,
  CurrentSubscription,
  CurrentSubscriptionResponse,
  PlatformSubscriptionPlan,
  PlatformSubscriptionPlanResponse,
  SubscribePlanResponse
} from "@repo/types";
import { apiClient } from "./base/http";

export const fetchSubscriptionPlans = async (): Promise<PlatformSubscriptionPlan[]> => {
  const { data } = await apiClient.get<PlatformSubscriptionPlanResponse>("/api/auth/subscription/plans");
  if (!data.success) {
    throw new Error("無法取得方案列表");
  }
  return data.plans;
};

export const fetchCurrentSubscription = async (): Promise<CurrentSubscription> => {
  const { data } = await apiClient.get<CurrentSubscriptionResponse>("/api/auth/subscription/current");
  if (!data.success) {
    throw new Error("無法取得訂閱狀態");
  }
  return data.subscription;
};

export interface SubscriptionPaymentChannel {
  code: string;
  label: string;
  description: string;
  channel_type: "manual" | "gateway";
  reference_label: string;
  reference_required: boolean;
  reference_digits: number | null;
  account_details: Array<{ label: string; value: string }>;
  sort_order: number;
}

export const fetchSubscriptionPaymentChannels = async (): Promise<SubscriptionPaymentChannel[]> => {
  const { data } = await apiClient.get<{ success: boolean; channels: SubscriptionPaymentChannel[] }>(
    "/api/auth/subscription/payment-channels"
  );
  if (!data.success) {
    throw new Error("無法取得付款方式");
  }
  return data.channels;
};

export interface SubscribePlanPayload {
  plan_id: string;
  billing_cycle?: "monthly" | "annual";
  payment_method: string;
  payment_reference?: string;
  payment_note?: string;
  evidence_url?: string;
  extra_staff_count?: number;
  extra_services_count?: number;
  extra_products_count?: number;
  extra_devices_count?: number;
  points_to_deduct?: number;
}

export const subscribeToPlan = async (payload: SubscribePlanPayload) => {
  const { data } = await apiClient.post<SubscribePlanResponse>("/api/auth/subscription/subscribe", payload);
  if (!data.success) {
    throw new Error(data.message ?? "訂閱方案失敗");
  }
  return data;
};

/** 已排定取消的訂閱選回原方案：撤銷取消排程、照常續用 */
export const resumeSubscription = async () => {
  const { data } = await apiClient.post<{ success: boolean; message?: string }>("/api/auth/subscription/resume", {});
  if (!data.success) {
    throw new Error(data.message ?? "恢復訂閱失敗");
  }
  return data;
};

export interface CancelSubscriptionPayload {
  reason?: string;
  transaction_id?: string;
}

export const cancelSubscription = async (payload?: CancelSubscriptionPayload) => {
  const { data } = await apiClient.post<CancelSubscriptionResponse>(
    "/api/auth/subscription/cancel",
    payload ?? {}
  );
  if (!data.success) {
    throw new Error(data.message ?? "取消訂閱失敗");
  }
  return data;
};

export const updateTransactionReference = async (transactionId: string, paymentReference: string) => {
  const { data } = await apiClient.patch<{ success: boolean; message: string }>(
    `/api/auth/subscription/transaction/${transactionId}/reference`,
    { payment_reference: paymentReference }
  );
  if (!data.success) {
    throw new Error(data.message ?? "更新付款參考失敗");
  }
  return data;
};

export interface TrialModulesResponse {
  success: boolean;
  modules: string[];
}

export const fetchTrialModules = async (): Promise<string[]> => {
  const { data } = await apiClient.get<TrialModulesResponse>("/api/auth/trial-modules");
  return data.modules ?? [];
};

export interface ResourceUsage {
  staffs: number;
  services: number;
  products: number;
  customers: number;
  appointments_this_month: number;
  devices: number;
}

export interface ResourceLimits {
  max_staffs: number;
  max_services: number;
  max_products: number;
  max_customers: number;
  max_appointments_per_month: number;
  max_devices: number;
}

export interface UsageResponse {
  success: boolean;
  usage: ResourceUsage;
  limits: ResourceLimits;
}

export const fetchUsage = async (): Promise<UsageResponse> => {
  const { data } = await apiClient.get<UsageResponse>("/api/auth/usage");
  return data;
};

// ─── 資源加購／調降申請（員工／服務項目／商品／設備）───
// 送出後需 platform-admin 審核通過才會反映到 ResourceLimits / 每月計費金額。

export interface AddonRequestPayload {
  extra_staff_count?: number;
  extra_services_count?: number;
  extra_products_count?: number;
  extra_devices_count?: number;
}

export interface AddonRequest {
  id: string;
  status: "pending" | "awaiting_payment" | "payment_submitted" | "approved" | "rejected" | "cancelled";
  amount_delta: number;
  new_monthly_price: number;
  requested_extra_staff_count?: number | null;
  requested_extra_services_count?: number | null;
  requested_extra_products_count?: number | null;
  requested_extra_devices_count?: number | null;
  note?: string | null;
  review_reason?: string | null;
  payment_reference?: string | null;
  payment_note?: string | null;
  evidence_url?: string | null;
  payment_requested_at?: string | null;
  payment_submitted_at?: string | null;
  created_at?: string;
}

export interface AddonRequestResponse {
  success: boolean;
  message?: string;
  request: Pick<AddonRequest, "id" | "status" | "amount_delta" | "new_monthly_price"> | null;
}

export const requestSubscriptionAddon = async (payload: AddonRequestPayload) => {
  const { data } = await apiClient.post<AddonRequestResponse>(
    "/api/auth/subscription/addons/request",
    payload
  );
  if (!data.success) {
    throw new Error(data.message ?? "送出加購申請失敗");
  }
  return data;
};

export interface AddonRequestStatus {
  request: AddonRequest | null;
  // 目前生效訂閱已購買的加購數量（跟這次 pending 申請無關，用來當
  // +/- 控制項的起始值）。
  current_extra_counts: {
    staff: number;
    services: number;
    products: number;
    devices: number;
  };
}

export const fetchPendingAddonRequest = async (): Promise<AddonRequestStatus> => {
  const { data } = await apiClient.get<{ success: boolean } & AddonRequestStatus>(
    "/api/auth/subscription/addons/request"
  );
  return {
    request: data.request ?? null,
    current_extra_counts: data.current_extra_counts ?? { staff: 0, services: 0, products: 0, devices: 0 },
  };
};

export interface SubmitAddonPaymentPayload {
  payment_reference: string;
  payment_method?: string;
  payment_note?: string;
  evidence_url?: string;
}

export const submitAddonPayment = async (requestId: string, payload: SubmitAddonPaymentPayload) => {
  const { data } = await apiClient.post<{ success: boolean; message?: string; request: { id: string; status: string } | null }>(
    `/api/auth/subscription/addons/request/${requestId}/payment`,
    payload
  );
  if (!data.success) {
    throw new Error(data.message ?? "回報加購付款失敗");
  }
  return data;
};

// ─── 每月付款回報（匯款制）───
// 租戶回報「本月已匯款＋參考碼」，需 platform-admin 確認收款後才會延長
// 訂閱到期日；確認前後端都不會動到方案/加購額度/計費金額。

export interface PaymentReportPayload {
  payment_reference: string;
  payment_method?: string;
  payment_note?: string;
  evidence_url?: string;
}

export interface PaymentReport {
  id: string;
  payment_reference: string;
  payment_method?: string | null;
  payment_note?: string | null;
  evidence_url?: string | null;
  amount_due: number;
  status: "pending" | "confirmed" | "rejected";
  period_extended_to?: string | null;
  review_reason?: string | null;
  created_at: string;
}

export interface SubmitPaymentReportResponse {
  success: boolean;
  message?: string;
  report: { id: string; status: "pending" } | null;
}

export const submitPaymentReport = async (payload: PaymentReportPayload) => {
  const { data } = await apiClient.post<SubmitPaymentReportResponse>(
    "/api/auth/subscription/payment-reports",
    payload
  );
  if (!data.success) {
    throw new Error(data.message ?? "送出付款回報失敗");
  }
  return data;
};

export const fetchPaymentReports = async (): Promise<PaymentReport[]> => {
  const { data } = await apiClient.get<{ success: boolean; reports: PaymentReport[] }>(
    "/api/auth/subscription/payment-reports"
  );
  return data.reports ?? [];
};
